import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  TextField,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// Components
import LoadingButton from "../utils/LoadingButton";
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";

// Context
import { useAuth } from "../../contexts/AuthContext";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Localization
  const { t } = useTranslation();

  // Context
  const { signIn, signInWithOtp, user } = useAuth();
  const { showNotification } = useNotification();

  // Hooks
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      showNotification(t("signIn.emailEmptyError"), "error");
      return;
    }

    if (usePassword && !password.trim()) {
      showNotification(t("signIn.passwordEmptyError"), "error");
      return;
    }

    setIsLoading(true);
    try {
      let error;
      if (usePassword) {
        // Sign in with email and password
        ({ error } = await signIn(email, password));
      } else {
        // Sign in with OTP
        ({ error } = await signInWithOtp(email));
      }

      if (error) {
        console.log(error.message);
        if (error.code === "otp_disabled") {
          navigate("/signup");
          return;
        }
        if (error.code === "invalid_credentials") {
          showNotification(t("signIn.invalidCredentials"), "error");
          return;
        } else {
          throw error;
        }
      }

      trackEvent("signin-signed_in", {
        method: usePassword ? "password" : "magic_link",
      });

      if (!usePassword) {
        showNotification(t("signIn.notificationSuccess"), "success");
      }
    } catch (error) {
      showNotification(t("signIn.notificationError"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default }}>
      <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4, position: "relative" }}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 4 }}>
            <a href="https://getkoubi.com">
              <img src="/logo819x190.png" alt="Koubi Logo" style={{ maxWidth: "150px" }} />
            </a>
          </Box>
          <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: theme.palette.mode === 'dark' ? '#fff' : '#141b2d' }}>
            {t("signIn.signInTitle", "Welcome Back")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {t("signIn.signInSubtitle", "Please sign in to your account to continue.")}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t("signIn.emailLabel")}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />

            {usePassword && (
              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                label={t("signIn.password")}
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={usePassword}
                  onChange={() => setUsePassword(!usePassword)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" sx={{ userSelect: "none" }}>
                  {usePassword ? t("signIn.useMagicLink") : t("signIn.usePassword")}
                </Typography>
              }
              sx={{ mb: 3 }}
            />

            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ 
                py: 1.5, 
                mb: 3, 
                background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                fontWeight: "bold",
                "&:hover": {
                  background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)",
                }
              }}
              onClick={handleSubmit}
              loading={isLoading}
            >
              {usePassword
                ? t("signIn.signInButton")
                : t("signIn.signInButtonMagicLink")}
            </LoadingButton>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("signIn.noAccount")}{" "}
                <Link to="/signup" style={{ color: "#7949E7", textDecoration: "none", fontWeight: "bold" }}>
                  {t("signIn.signUpLink")}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} md={6} sx={{ 
        display: { xs: "none", md: "flex" }, 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center", 
        background: "linear-gradient(135deg, #141b2d 0%, #463575 100%)",
        position: "relative",
        overflow: "hidden"
      }}>
        <Box sx={{
          position: "absolute",
          top: "-10%",
          right: "-10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(121,73,231,0.2) 0%, rgba(20,27,45,0) 70%)",
        }} />
        <Box sx={{
          position: "absolute",
          bottom: "-5%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(76,206,172,0.15) 0%, rgba(20,27,45,0) 70%)",
        }} />
        <Box sx={{ zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <a href="https://getkoubi.com">
            <img
              src="/logo819x190.png"
              alt="Koubi Logo"
              style={{
                maxWidth: "240px",
                filter: "brightness(0) invert(1)",
                marginBottom: "2rem"
              }}
            />
          </a>
          <Typography variant="h4" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: "300", letterSpacing: "1px", textAlign: "center", px: 4 }}>
            {t("signIn.heroText", "Your intelligent business dashboard.")}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SignIn;
